import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Pencil, Power, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { LogoMark, RelativeTime, StatusBadge } from '../../../../components/design-system/admin';
import { AdminUser, fullName, initials, isLockedRow, isUserEnabled } from '../users.utils';

export interface UsersAsListProps {
    /** The rows of the current page. */
    users: AdminUser[];
    handleEditUser: (user: AdminUser) => void;
    onDelete: (user: AdminUser) => void;
    onToggleStatus: (user: AdminUser) => void;
    /** "Ardealy staff" / "Vendor admin" for a raw role. */
    roleLabel: (role: string) => string;
}

/**
 * The users table: who, which vendor, what role, whether they can sign in.
 * Staff rows (first role SUPER_ADMIN) are read-only here: no link, no menu.
 */
export const UsersAsList = ({ users, handleEditUser, onDelete, onToggleStatus, roleLabel }: UsersAsListProps) => {
    const { t } = useTranslation();

    return (
        <div className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="ps-4">{t('ADMIN_USERS.COL.USER')}</TableHead>
                        <TableHead>{t('ADMIN_USERS.COL.VENDOR')}</TableHead>
                        <TableHead>{t('ADMIN_USERS.COL.ROLE')}</TableHead>
                        <TableHead>{t('ADMIN_USERS.COL.STATUS')}</TableHead>
                        <TableHead>{t('ADMIN_USERS.COL.CREATED')}</TableHead>
                        <TableHead className="w-12 pe-4">
                            <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {users.map((user, index) => {
                        const locked = isLockedRow(user);
                        const enabled = isUserEnabled(user);
                        const name = fullName(user);
                        const label = name || user.email || t('ADMIN_USERS.UNKNOWN');
                        const vendorId = user.vendor?._id;

                        return (
                            <TableRow key={user._id || index} data-testid="user-row" className={cn(!enabled && 'text-muted-foreground')}>
                                <TableCell className="ps-4">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <Avatar size="sm">
                                            <AvatarFallback>{initials(user)}</AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0">
                                            {locked ? (
                                                <span className="block truncate text-sm font-medium">{label}</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditUser(user)}
                                                    className="block max-w-full truncate text-start text-sm font-medium hover:underline focus-visible:underline focus-visible:outline-none"
                                                >
                                                    {label}
                                                </button>
                                            )}
                                            {name && user.email && (
                                                <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                                            )}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {user.vendor ? (
                                        <Link
                                            to={`/vendors/${vendorId}/edit`}
                                            className="inline-flex max-w-56 items-center gap-2 hover:underline"
                                            aria-label={t('ADMIN_USERS.OPEN_VENDOR', { name: user.vendor.name })}
                                        >
                                            <LogoMark src={user.vendor.config?.logo} name={user.vendor.name} className="size-6 rounded-md" />
                                            <span className="truncate text-sm">{user.vendor.name}</span>
                                        </Link>
                                    ) : (
                                        <span className="text-muted-foreground">—</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <span className="flex flex-wrap gap-1">
                                        {user.roles?.map((role) => (
                                            <Badge key={role} variant={role === 'SUPER_ADMIN' ? 'secondary' : 'outline'}>
                                                {roleLabel(role)}
                                            </Badge>
                                        ))}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <StatusBadge tone={enabled ? 'success' : 'danger'}>
                                        {enabled ? t('ADMIN_SHELL.COMMON.ENABLED') : t('ADMIN_SHELL.COMMON.DISABLED')}
                                    </StatusBadge>
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {user.createdAt ? <RelativeTime value={user.createdAt} /> : '—'}
                                </TableCell>
                                <TableCell className="pe-4 text-end">
                                    {!locked && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon-sm" aria-label={t('ADMIN_USERS.ROW_ACTIONS', { name: label })}>
                                                    <MoreHorizontal />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-44">
                                                <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                                    <Pencil />
                                                    {t('ADMIN_USERS.MENU.EDIT')}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => onToggleStatus(user)}>
                                                    <Power />
                                                    {enabled ? t('ADMIN_USERS.MENU.DISABLE') : t('ADMIN_USERS.MENU.ENABLE')}
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem variant="destructive" onClick={() => onDelete(user)}>
                                                    <Trash2 />
                                                    {t('ADMIN_USERS.MENU.DELETE')}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};
